import type {
  AppPermission,
  DocumentItem,
  InterfaceMode,
  ParticipantAccessProfile,
  ParticipantAccountType,
  ParticipantSpace,
  Role,
  TeamMemberAccess,
  User,
} from '../types'

/* ------------------------------------------------------------------ *
 * Permission catalogue
 * ------------------------------------------------------------------ */

/** Canonical order — used to keep resolved permission arrays stable. */
export const ALL_APP_PERMISSIONS: AppPermission[] = [
  'view_today',
  'view_calendar',
  'view_tasks',
  'view_updates',
  'view_messages',
  'view_documents',
  'view_goals',
  'view_team',
  'manage_team',
  'view_onboarding',
  'manage_onboarding',
  'view_handover',
  'view_profile',
  'view_settings',
  'view_funding',
  'manage_funding',
  'manage_permissions',
  'manage_cover',
  'cover_shifts',
]

export interface PermissionMeta {
  label: string
  description: string
  group: 'Daily support' | 'Information' | 'People' | 'Money' | 'Account'
  /** Permissions that only make sense alongside their read counterpart. */
  requires?: AppPermission
}

/** Human wording for the future "who can do what" screens. */
export const PERMISSION_META: Record<AppPermission, PermissionMeta> = {
  view_today: { label: 'Today', description: "See today's plan", group: 'Daily support' },
  view_calendar: { label: 'Calendar', description: 'See shifts and appointments', group: 'Daily support' },
  view_tasks: { label: 'Tasks', description: 'See and complete tasks', group: 'Daily support' },
  view_updates: { label: 'Updates', description: 'See the team update feed', group: 'Daily support' },
  view_messages: { label: 'Messages', description: 'Message people on the team', group: 'Daily support' },
  view_handover: { label: 'Handover', description: 'Read shift handovers in Team Updates', group: 'Daily support' },
  manage_cover: {
    label: 'Arrange cover',
    description: 'Find replacement cover and send cover requests',
    group: 'Daily support',
    requires: 'view_calendar',
  },
  cover_shifts: {
    label: 'Shift cover',
    description: 'Withdraw from a shift and answer cover requests',
    group: 'Daily support',
    requires: 'view_calendar',
  },
  view_documents: { label: 'Documents', description: 'Open shared documents', group: 'Information' },
  view_goals: { label: 'Goals', description: 'See goals and progress', group: 'Information' },
  view_team: { label: 'Team directory', description: 'See who is on the team', group: 'People' },
  manage_team: {
    label: 'Manage team',
    description: 'Add or remove people from the team',
    group: 'People',
    requires: 'view_team',
  },
  view_onboarding: { label: 'Onboarding', description: 'See onboarding and training', group: 'People' },
  manage_onboarding: {
    label: 'Manage onboarding',
    description: 'Assign training and sign people off',
    group: 'People',
    requires: 'view_onboarding',
  },
  view_funding: { label: 'Funding', description: 'See plan funding and budgets', group: 'Money' },
  manage_funding: {
    label: 'Manage funding',
    description: 'Record spending and change budgets',
    group: 'Money',
    requires: 'view_funding',
  },
  view_profile: { label: 'Profile', description: 'See and edit their own profile', group: 'Account' },
  view_settings: { label: 'Settings', description: 'Change their own notification settings', group: 'Account' },
  manage_permissions: {
    label: 'Manage access',
    description: 'Decide what everyone else can see and do',
    group: 'Account',
  },
}

/* ------------------------------------------------------------------ *
 * Core helper
 * ------------------------------------------------------------------ */

export function hasPermission(
  permissions: AppPermission[],
  permission: AppPermission,
): boolean {
  return permissions.includes(permission)
}

export function hasAnyPermission(permissions: AppPermission[], required: AppPermission[]): boolean {
  return required.some((p) => hasPermission(permissions, p))
}

export function hasAllPermissions(permissions: AppPermission[], required: AppPermission[]): boolean {
  return required.every((p) => hasPermission(permissions, p))
}

/* ------------------------------------------------------------------ *
 * Defaults
 *
 * These are starting points only. The participant / nominee owns the final
 * answer — see resolveTeamMemberPermissions().
 * ------------------------------------------------------------------ */

const EVERY_PERMISSION = ALL_APP_PERMISSIONS

/** What a support worker sees out of the box. */
const SUPPORT_BASELINE: AppPermission[] = [
  'view_today',
  'view_calendar',
  'view_tasks',
  'view_updates',
  'view_messages',
  'view_documents',
  'view_goals',
  'view_team',
  'view_onboarding',
  'view_handover',
  'view_profile',
  'view_settings',
]

/** Allied health are not part of worker onboarding unless explicitly granted access. */
const ALLIED_HEALTH_BASELINE: AppPermission[] = SUPPORT_BASELINE.filter(
  (permission) => permission !== 'view_onboarding',
)

export const DEFAULT_ROLE_PERMISSIONS: Record<Role, AppPermission[]> = {
  // The participant owns the space. Youth accounts start narrower — see
  // defaultParticipantPermissions().
  participant: EVERY_PERMISSION,
  // A rep / nominee acts on the participant's behalf, so they mirror the owner.
  rep: EVERY_PERMISSION,
  // Coordination work needs onboarding and funding visibility, but never
  // control over who is on the team or what they can access. Arranging
  // replacement cover stays with the participant / nominee unless they
  // explicitly hand `manage_cover` to their coordinator.
  coordinator: [...SUPPORT_BASELINE, 'manage_onboarding', 'view_funding'],
  // Allied health sit in the wider team: no worker onboarding or rostering by default.
  allied_health: ALLIED_HEALTH_BASELINE,
  // Support workers are the people who actually work the shifts, so they are
  // the only role that can withdraw from one or answer a cover request.
  support_worker: [...SUPPORT_BASELINE, 'cover_shifts'],
}

export function defaultPermissionsForRole(role: Role): AppPermission[] {
  return DEFAULT_ROLE_PERMISSIONS[role] ?? []
}

/**
 * Youth accounts keep everything they need day to day, but administrative
 * controls (team membership, access decisions, money) sit with the nominee
 * until the participant or nominee explicitly hands them over.
 */
const YOUTH_WITHHELD: AppPermission[] = [
  'manage_team',
  'manage_permissions',
  'manage_funding',
  'manage_onboarding',
]

export function defaultParticipantPermissions(accountType: ParticipantAccountType): AppPermission[] {
  if (accountType === 'youth') {
    return ALL_APP_PERMISSIONS.filter((p) => !YOUTH_WITHHELD.includes(p))
  }
  return [...ALL_APP_PERMISSIONS]
}

/**
 * Permissions introduced after the first spaces were saved. A stored access
 * profile lists the owner's permissions explicitly, so without this a new
 * capability would read as "the participant turned it off" the moment it
 * shipped. Team members need no equivalent: their record stores only the
 * differences from role defaults, so new defaults reach them automatically.
 */
const PERMISSIONS_ADDED_LATER: AppPermission[] = ['manage_cover', 'cover_shifts']

export function backfillParticipantPermissions(
  stored: AppPermission[],
  accountType: ParticipantAccountType,
): AppPermission[] {
  const allowedForType = defaultParticipantPermissions(accountType)
  const set = new Set(stored)
  PERMISSIONS_ADDED_LATER.filter((p) => allowedForType.includes(p)).forEach((p) => set.add(p))
  return ALL_APP_PERMISSIONS.filter((p) => set.has(p))
}

export function defaultAccessProfile(
  accountType: ParticipantAccountType = 'adult',
  interfaceMode: InterfaceMode = 'standard',
): ParticipantAccessProfile {
  return {
    accountType,
    interfaceMode,
    companionEnabled: false,
    permissions: defaultParticipantPermissions(accountType),
  }
}

export function createTeamMemberAccess(userId: string): TeamMemberAccess {
  return { userId, useRoleDefaults: true, granted: [], revoked: [] }
}

/* ------------------------------------------------------------------ *
 * Resolution
 * ------------------------------------------------------------------ */

/**
 * effective = (roleDefaults ∪ granted) \ revoked
 *
 * `access` is whatever the participant / nominee has decided for this person.
 * Passing undefined means "no decision recorded yet" → pure role defaults.
 */
export function resolveTeamMemberPermissions(
  role: Role,
  access?: TeamMemberAccess,
): AppPermission[] {
  const base = access && !access.useRoleDefaults ? [] : defaultPermissionsForRole(role)
  const set = new Set<AppPermission>([...base, ...(access?.granted ?? [])])
  ;(access?.revoked ?? []).forEach((p) => set.delete(p))
  // Keep catalogue order so comparisons and UI lists stay predictable.
  return ALL_APP_PERMISSIONS.filter((p) => set.has(p))
}

/**
 * The single place the app answers "what can this person do in this space?".
 * The participant reads from their own access profile; everyone else is
 * resolved from role defaults plus the participant's overrides.
 */
export function resolvePermissionsForUser(user: User, space: ParticipantSpace): AppPermission[] {
  if (user.id === space.participantUserId) {
    return ALL_APP_PERMISSIONS.filter((p) => space.accessProfile.permissions.includes(p))
  }
  const access = space.teamAccess.find((a) => a.userId === user.id)
  return resolveTeamMemberPermissions(user.role, access)
}

/** Turn an effective permission set back into a stored override record. */
export function accessFromPermissions(
  userId: string,
  role: Role,
  permissions: AppPermission[],
  existing?: TeamMemberAccess,
): TeamMemberAccess {
  const defaults = defaultPermissionsForRole(role)
  return {
    ...createTeamMemberAccess(userId),
    ...existing,
    userId,
    useRoleDefaults: true,
    granted: permissions.filter((p) => !defaults.includes(p)),
    revoked: defaults.filter((p) => !permissions.includes(p)),
  }
}

/* ------------------------------------------------------------------ *
 * Document visibility
 *
 * Sharing is per-document and never implied by a role. A worker's private
 * document stays with that worker — being on the team, or even owning the
 * space, does not silently open someone else's private file.
 * ------------------------------------------------------------------ */

/** Roles that hold the participant's own records rather than a worker's. */
const SPACE_OWNER_ROLES: Role[] = ['participant', 'rep']

export function canAccessDocument(
  viewer: User,
  doc: DocumentItem,
  permissions: AppPermission[],
  uploaderRole: Role | undefined,
): boolean {
  if (!hasPermission(permissions, 'view_documents')) return false
  // Your own upload is always yours.
  if (doc.uploadedByUserId === viewer.id) return true

  const uploaderOwnsSpace = uploaderRole ? SPACE_OWNER_ROLES.includes(uploaderRole) : false

  if (doc.access === 'private') {
    // Participant-owned private files stay with the participant and nominee.
    // Worker-private files stay with the worker who uploaded them.
    return uploaderOwnsSpace && SPACE_OWNER_ROLES.includes(viewer.role)
  }
  if (doc.access === 'custom') {
    return doc.allowedUserIds.includes(viewer.id) || SPACE_OWNER_ROLES.includes(viewer.role)
  }
  return true // 'everyone'
}

/* ------------------------------------------------------------------ *
 * Route mapping
 *
 * One source of truth shared by the router guard, the bottom nav and the
 * More menu, so a hidden button and a blocked route can never disagree.
 * Routes absent from this map are open to anyone signed in.
 * ------------------------------------------------------------------ */

export const ROUTE_PERMISSIONS: Record<string, AppPermission> = {
  '/today': 'view_today',
  '/calendar': 'view_calendar',
  '/tasks': 'view_tasks',
  '/updates': 'view_updates',
  '/chats': 'view_messages',
  '/documents': 'view_documents',
  '/goals': 'view_goals',
  '/team': 'view_team',
  '/onboarding': 'view_onboarding',
  '/handover': 'view_handover',
  '/profile': 'view_profile',
  '/settings': 'view_settings',
}

/**
 * Screens only one kind of team member can open, whatever their permissions.
 *
 * Writing a handover is an account of a shift you personally worked, so the
 * screen belongs to the worker who was there. Everyone else the participant
 * has shared handovers with reads them in Team Updates instead — that is what
 * `view_handover` grants them.
 */
export const ROUTE_ROLES: Record<string, Role[]> = {
  '/handover': ['support_worker'],
}

/** Roles that record handovers. Reading them is a permission, not a role. */
export const HANDOVER_AUTHOR_ROLES: Role[] = ROUTE_ROLES['/handover']

export function canRecordHandover(role: Role, permissions: AppPermission[]): boolean {
  return HANDOVER_AUTHOR_ROLES.includes(role) && hasPermission(permissions, 'view_handover')
}

/** Whether handovers should be shown to this reader inside Team Updates. */
export function canReadHandovers(permissions: AppPermission[]): boolean {
  return hasAllPermissions(permissions, ['view_updates', 'view_handover'])
}

export function canAccessRoute(permissions: AppPermission[], path: string, role?: Role): boolean {
  const required = ROUTE_PERMISSIONS[path]
  if (required && !hasPermission(permissions, required)) return false
  const allowedRoles = ROUTE_ROLES[path]
  // Without a role the caller is only asking the permission question.
  if (allowedRoles && role && !allowedRoles.includes(role)) return false
  return true
}

/* ------------------------------------------------------------------ *
 * Backend enforcement map
 *
 * Hiding a nav button is a convenience, not a control. Each permission below
 * names the data it guards so the same rule can be expressed as a Supabase
 * Row Level Security policy once the database exists. The intended shape:
 *
 *   - every row carries participant_user_id (the space it belongs to)
 *   - team_access(participant_user_id, user_id, use_role_defaults, granted[], revoked[])
 *   - a SQL function has_permission(space uuid, perm text) mirrors
 *     resolveTeamMemberPermissions() above
 *   - policies read: USING (has_permission(participant_user_id, 'view_tasks'))
 *
 * Keeping this table beside the client logic means the frontend and the future
 * policies are generated from one list rather than drifting apart.
 * ------------------------------------------------------------------ */

export interface PermissionBackendTarget {
  /** Tables the permission guards. */
  tables: string[]
  /** Which SQL operations the policy should cover. */
  operations: ('select' | 'insert' | 'update' | 'delete')[]
}

export const PERMISSION_BACKEND_MAP: Record<AppPermission, PermissionBackendTarget> = {
  view_today: { tables: ['events', 'tasks', 'updates'], operations: ['select'] },
  view_calendar: { tables: ['events', 'event_assignees'], operations: ['select'] },
  view_tasks: { tables: ['tasks'], operations: ['select', 'update'] },
  view_updates: { tables: ['updates', 'update_checklist_items'], operations: ['select', 'insert'] },
  view_messages: { tables: ['messages', 'group_threads'], operations: ['select', 'insert'] },
  view_documents: { tables: ['documents', 'document_access'], operations: ['select'] },
  view_goals: { tables: ['goals'], operations: ['select'] },
  view_team: { tables: ['team_members'], operations: ['select'] },
  manage_team: { tables: ['team_members'], operations: ['insert', 'update', 'delete'] },
  view_onboarding: { tables: ['onboarding_items', 'onboarding_progress'], operations: ['select'] },
  manage_onboarding: { tables: ['onboarding_items', 'onboarding_progress'], operations: ['insert', 'update', 'delete'] },
  // Handovers are rows in `updates` carrying a structured note, so reading one
  // is a filter on that table rather than a second feed. Insert stays with the
  // support worker who worked the shift — see ROUTE_ROLES above.
  view_handover: { tables: ['updates', 'handover_notes'], operations: ['select'] },
  view_profile: { tables: ['profiles', 'availability'], operations: ['select', 'update'] },
  view_settings: { tables: ['notification_prefs'], operations: ['select', 'update'] },
  view_funding: { tables: ['funding_budgets', 'funding_transactions'], operations: ['select'] },
  manage_funding: { tables: ['funding_budgets', 'funding_transactions'], operations: ['insert', 'update', 'delete'] },
  manage_permissions: { tables: ['team_access', 'participant_access_profile'], operations: ['select', 'update'] },
  // Cover requests carry the participant_user_id of the space, so the same
  // policy shape applies: only the space owner (or a member holding
  // manage_cover) may create them, and only the named worker may respond.
  manage_cover: { tables: ['cover_requests', 'events'], operations: ['select', 'insert', 'update'] },
  cover_shifts: { tables: ['cover_requests', 'event_assignees'], operations: ['select', 'update'] },
}

